// src/pages/Profile/Profile3.tsx

import { FC } from 'react';
import styles from '../../css/ProfilePage.module.css';
import { motion, Variants } from 'framer-motion';
import { ArrowLeft, Linkedin, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

// Import Image
import ProfileImg3 from '../../assets/images/ProfileImage3.jpeg';

const Profile3: FC = () => {
    
    const fadeInUp: Variants = {
        hidden: { opacity: 0, y: 20 },
        visible: { 
            opacity: 1, 
            y: 0, 
            transition: { duration: 0.6, ease: "easeOut" } 
        }
    };

    const staggerContainer: Variants = {
        visible: {
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    return (
        <div className={styles.profilePage}>
            
            <section className={styles.heroSection}>
                <div className={styles.gridBackground}></div>
                <div className={styles.container}>
                    
                    {/* Back Navigation */}
                    <div className={styles.backLinkWrapper}>
                        <Link to="/about" className={styles.backLink}>
                            <ArrowLeft size={16} /> Back to Leadership
                        </Link>
                    </div>

                    <div className={styles.profileHeader}>
                        
                        {/* Left: Image & Socials */}
                        <motion.div 
                            className={styles.imageColumn}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5 }}
                        >
                            <div className={styles.profileImageWrapper}>
                                <img src={ProfileImg3} alt="Silas Adomako" className={styles.profileImage} />
                            </div>
                            <div className={styles.socialRow}>
                                <a 
                                    href="https://www.linkedin.com/in/silas-adomako/" 
                                    target="_blank" 
                                    rel="noreferrer" 
                                    className={styles.socialBtn}
                                    aria-label="LinkedIn Profile"
                                >
                                    <Linkedin size={20} />
                                </a>
                                <a 
                                    href="mailto:contact@otatech.com" 
                                    className={styles.socialBtn}
                                    aria-label="Contact"
                                >
                                    <Mail size={20} />
                                </a>
                            </div>
                        </motion.div>

                        {/* Right: Biography */}
                        <motion.div 
                            className={styles.infoColumn}
                            initial="hidden"
                            animate="visible"
                            variants={staggerContainer}
                        >
                            <motion.h1 className={styles.name} variants={fadeInUp}>
                                Silas Adomako
                            </motion.h1>
                            <motion.div className={styles.title} variants={fadeInUp}>
                                Director, Finance & Product Startup Development
                            </motion.div>

                            {/* New Section: Role at OTA TECH */}
                            <motion.div className={styles.bioSection} variants={fadeInUp}>
                                <h2>Impact at OTA TECH</h2>
                                <p className={styles.bioText}>
                                    As the Director of Finance & Product Startup Development, Silas acts as the bridge between complex financial logic and software engineering. He leads the <strong>architectural design of the financial reporting modules</strong> within OTA ERP, ensuring they meet the rigorous standards required by CFOs and investors.
                                </p>
                                <p className={styles.bioText}>
                                    Additionally, he spearheads the <strong>Go-To-Market (GTM) strategy</strong> for the platform's commercial launch, defining customer segmentation and ensuring the product delivers immediate ROI for scaling businesses.
                                </p>
                            </motion.div>

                            <motion.div className={styles.bioSection} variants={fadeInUp}>
                                <h2>Professional Overview</h2>
                                <p className={styles.bioText}>
                                    He works with founders, SMBs, and finance leaders to build the financial and operational backbone for scaling businesses. His approach combines <strong>finance clarity, project discipline, and growth strategy</strong> so teams can scale without chaos.
                                </p>
                            </motion.div>

                            <motion.div className={styles.bioSection} variants={fadeInUp}>
                                <h2>Finance Systems Architecture</h2>
                                <p className={styles.bioText}>
                                    Most startups outgrow basic ERPs like QuickBooks but aren't ready for NetSuite. Silas specializes in building <strong>lean financial models and dashboards</strong> that bridge this gap:
                                </p>
                                <p className={styles.bioText}>
                                    • Consolidated multi-entity & multi-currency reporting (with FX adjustments).<br/>
                                    • 13-week forward-looking cash flow forecasting.<br/>
                                    • Precise tracking of unit economics, product profitability, and contribution margins.<br/>
                                    • Investor-ready reporting packs tailored for board meetings and VC due diligence.
                                </p>
                                <p className={styles.bioText}>
                                    <em>Notable Impact:</em> He deployed tools used by a YC-backed startup for over 2 years to deliver investor reporting without needing to migrate to NetSuite.
                                </p>
                            </motion.div>

                            <motion.div className={styles.bioSection} variants={fadeInUp}>
                                <h2>Project Appraisal & Feasibility</h2>
                                <p className={styles.bioText}>
                                    Big investments need bankable analysis. He utilizes an <strong>Integrated Project Appraisal Model</strong> to run cost-benefit analysis, 3-statement feasibility modeling, and rigorous sensitivity testing.
                                </p>
                                <p className={styles.bioText}>
                                    This provides founders and boards with the confidence that projects are viable before committing capital. He recently completed a bankable feasibility study and full financial model for a public building project in Ghana using the <strong>Integrated Investment Appraisal (Glenn Jenkins)</strong> framework.
                                </p>
                            </motion.div>

                            <motion.div className={styles.bioSection} variants={fadeInUp}>
                                <h2>GTM & Revenue Strategy</h2>
                                <p className={styles.bioText}>
                                    Finance is only powerful when it enables growth. He partners with businesses to design and execute:
                                </p>
                                <p className={styles.bioText}>
                                    • GTM strategies for product launches and new market entry.<br/>
                                    • Pricing models linked directly to unit economics.<br/>
                                    • Customer segmentation and retention strategies.
                                </p>
                                <p className={styles.bioText}>
                                    <em>Case Study:</em> He led a “Sales Drive” GTM strategy that helped scale a distribution business into one of Ghana’s largest in under 2 years, driving <strong>10 consecutive months of unit economics profitability</strong> through cost discipline and contract renegotiation.
                                </p>
                            </motion.div>

                        </motion.div>
                    </div>
                </div>
                <div className={styles.heroFade}></div>
            </section>
        </div>
    );
};

export default Profile3;